import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import LocIcon from "../../assets/icons/locIcon.svg";
import TimeIcon from "../../assets/icons/timeicon.svg";
import BagIcon from "../../assets/icons/bagIcon.svg";
import PaperIcon from "../../assets/icons/paperIcon.svg";
import { jobsEndpoint } from "../../constants";

function InfoCard({ label, children }) {
  return (
    <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
      <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
        <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
          <p className="flex-1 text-[#181D27] text-sm font-semibold">{label}</p>
        </div>
        <div className="flex self-stretch justify-start items-start flex-col gap-5 p-[20px] bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)] relative">
          {children}
        </div>
      </div>
    </div>
  );
}

function CandidateJobsDetails() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [job, setJobPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    const fetchJobPost = async () => {
      try {
        const response = await axios.get(`${jobsEndpoint}${id}/details/`, {
          headers: {
            'Authorization': `Token ${token}`,
          },
        });
        setJobPost(response.data);
      } catch (error) {
        console.error(error);
        setError('Failed to load job details.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobPost();
  }, [id]);

  const handleApplyClick = () => {
    navigate(`/candidate/job-apply/${id}`);
  };

  if (loading) return <p className="text-gray-500 text-sm p-6">Loading job details...</p>;
  if (error) return <p className="text-red-500 text-sm p-6">{error}</p>;
  if (!job) return null;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center">
          {job.logo
            ? <img src={job.logo} className="w-14 h-14 rounded-full object-cover" alt="company logo" />
            : <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs">Logo</div>
          }
          <div className="flex flex-col gap-1">
            <div className="flex gap-4 items-baseline">
              <h1 className="text-gray-800 text-xl font-semibold">{job.position}</h1>
              {job.employment_type_library && (
                <span className="border border-blue-300 bg-blue-100 text-blue-500 font-semibold px-3 py-1 rounded-xl text-xs flex gap-1 items-center">
                  <span>&bull;</span>
                  {job.employment_type_library}
                </span>
              )}
            </div>
            <p className="text-gray-500 font-normal text-xs">{job.company}</p>
          </div>
        </div>
        <button
          className="text-white text-sm font-semibold rounded-lg px-4 py-2 bg-[#19BFB7] cursor-pointer"
          onClick={handleApplyClick}
        >
          Apply Job
        </button>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-4 pt-1">
        <p className="text-gray-500 font-semibold text-xs flex gap-2 items-center">
          <img src={TimeIcon} alt="time icon" className="w-4 h-4" />
          {job.location && <span className="border-r border-gray-400 pr-2">{job.location}</span>}
          {job.time && <span>{job.time}</span>}
        </p>
        {job.type && (
          <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
            <img src={LocIcon} alt="loc icon" className="w-4 h-4" />
            {job.type}
          </span>
        )}
        {job.level && (
          <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
            <img src={BagIcon} alt="bag icon" className="w-4 h-4" />
            {job.level}
          </span>
        )}
        {job.num_applicants != null && (
          <span className="text-gray-500 font-semibold text-xs flex gap-1 items-center">
            <img src={PaperIcon} alt="paper icon" className="w-4 h-4" />
            {job.num_applicants > 100 ? '100+' : job.num_applicants}
          </span>
        )}
      </div>

      {/* Info cards */}
      <div className="flex items-center gap-8">
        <InfoCard label="Salary">
          <p className="text-gray-700 text-lg font-semibold">
            {job.offer_price
              ? <>{job.offer_price} <span className="text-xs ml-3 text-gray-500">/ {job.salary_period_library || 'month'}</span></>
              : <span className="text-gray-400 text-base">Not specified</span>
            }
          </p>
        </InfoCard>

        <InfoCard label="Date Posted">
          <p className="text-gray-700 text-lg font-semibold">
            {job.date_updated
              ? new Date(job.date_updated).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
              : '—'
            }
          </p>
        </InfoCard>

        <InfoCard label="Status">
          <p className="text-gray-700 text-lg font-semibold">Open</p>
        </InfoCard>
      </div>

      {/* Description & details */}
      <div className="mt-6 flex flex-col gap-10">

        {/* Job body / description */}
        {job.body && (
          <div className="flex flex-col gap-2">
            <h2 className="text-gray-700 font-semibold text-lg">Job Description</h2>
            <p className="text-gray-500 font-medium text-[16px] whitespace-pre-line">{job.body}</p>
          </div>
        )}

        {/* Requirements & Skills */}
        {job.requirements && (
          <div className="flex flex-col gap-7">
            <div className="flex gap-6 items-start">
              <InfoCard label="Job Requirements">
                <p className="text-gray-700 text-sm whitespace-pre-line">{job.requirements}</p>
              </InfoCard>
            </div>
          </div>
        )}

        {/* Compensation & Benefits */}
        {(job.other_benefits || job.bonuses_incentives) && (
          <div className="flex flex-col gap-5">
            <h2 className="text-gray-700 text-lg font-semibold">Compensation & Benefits</h2>
            <div className="flex gap-6">
              {job.bonuses_incentives && (
                <InfoCard label="Bonuses & Incentives">
                  <p className="text-gray-700 text-sm whitespace-pre-line">{job.bonuses_incentives}</p>
                </InfoCard>
              )}
              {job.other_benefits && (
                <InfoCard label="Other Benefits">
                  <p className="text-gray-700 text-sm whitespace-pre-line">{job.other_benefits}</p>
                </InfoCard>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default CandidateJobsDetails;